// Chat Store - Pinia - WhatsApp Business compliant
import { defineStore } from 'pinia'
import { chatHelpers, realtimeSubscriptions } from '@/plugins/supabase'
import audioService from '@/services/audioService'
import { useAuthStore } from './auth'

export const useChatStore = defineStore('chat', {
  state: () => ({
    conversations: [],
    messages: {},
    currentConversation: null,
    currentClinicId: null,

    conversationsLoading: false,
    conversationsFullyLoaded: false,
    messagesLoading: false,
    loadingOlderMessages: false,
    hasMoreMessages: {},
    sendingMessage: false,
    error: null,

    subscription: null,
    messageSubscription: null,

    soundsEnabled: true,
    soundVolume: 0.7
  }),

  getters: {
    conversationsForCurrentClinic: (state) =>
      state.conversations.filter(conv =>
        !state.currentClinicId || String(conv.clinic_id) === String(state.currentClinicId)
      ).sort((a, b) => new Date(b.last_message_time) - new Date(a.last_message_time)),

    currentMessages: (state) =>
      state.currentConversation
        ? (state.messages[state.currentConversation.id] || []).sort((a, b) =>
            new Date(a.created_at) - new Date(b.created_at)
          )
        : [],

    unreadCount: (state) =>
      state.conversations.reduce((total, conv) => total + (conv.unread_count || 0), 0),

    attentionNeededCount: (state) =>
      state.conversations.filter(conv => conv.needs_attention).length,

    conversationsNeedingAttention: (state) =>
      state.conversations.filter(conv => conv.needs_attention)
        .sort((a, b) => new Date(b.last_message_time) - new Date(a.last_message_time))
  },

  actions: {
    // Extraer IDs de clínicas autorizadas del usuario
    _getClinicIds() {
      if (this.currentClinicId) {
        return [String(this.currentClinicId)]
      }
      const authStore = useAuthStore()
      const userClinics = authStore.assignedClinics
      if (userClinics && userClinics.length > 0) {
        return userClinics.map(clinic =>
          typeof clinic === 'object' && clinic !== null ? String(clinic.id) : String(clinic)
        ).filter(Boolean)
      }
      return []
    },

    _mapConversation(conv) {
      return { ...conv, avatar: null, is_pinned: false, is_muted: false }
    },

    async loadConversations() {
      this.conversationsLoading = true
      this.conversationsFullyLoaded = false
      this.error = null

      try {
        const clinicIds = this._getClinicIds()
        if (!clinicIds || clinicIds.length === 0) {
          this.conversations = []
          this.conversationsFullyLoaded = true
          return
        }
        const PAGE = 30

        // Primera página: rápida, muestra la lista al instante
        const first = await chatHelpers.getConversations(clinicIds, { from: 0, size: PAGE })
        this.conversations = first.map(this._mapConversation)
        this.conversationsLoading = false // UI ya puede pintar

        // Si hay menos de PAGE, ya terminamos
        if (first.length < PAGE) {
          this.conversationsFullyLoaded = true
          return
        }

        // Micro queries en segundo plano para ir llenando
        let offset = PAGE
        while (true) {
          const batch = await chatHelpers.getConversations(clinicIds, { from: offset, size: PAGE })
          if (batch.length === 0) break

          const mapped = batch.map(this._mapConversation)
          // Agregar solo los que no existan ya (evitar duplicados por realtime)
          for (const conv of mapped) {
            if (!this.conversations.find(c => c.id === conv.id)) {
              this.conversations.push(conv)
            }
          }

          if (batch.length < PAGE) break
          offset += PAGE
        }

        this.conversationsFullyLoaded = true
      } catch (error) {
        console.error('Error loading conversations:', error)
        this.error = error.message || 'Error al cargar conversaciones'
        if (this.conversations.length === 0) this.conversations = []
      } finally {
        this.conversationsLoading = false
      }
    },

    _mapMessage(msg) {
      return {
        ...msg,
        read: true,
        status: msg.twilio_status || (msg.direction === 'outgoing' ? 'sent' : undefined),
        timestamp: msg.created_at
      }
    },

    async loadMessages(conversationId) {
      if (!conversationId) return

      this.messagesLoading = true
      this.error = null

      try {
        const conversation = this.conversations.find(c => c.id === conversationId)
        if (!conversation) {
          this.messages[conversationId] = []
          return
        }

        const messages = await chatHelpers.getMessages(conversationId)
        this.messages[conversationId] = messages.map(this._mapMessage)
        this.hasMoreMessages[conversationId] = messages.length >= 20

      } catch (error) {
        console.error('Error loading messages:', error)
        this.error = error.message || 'Error al cargar mensajes'
        this.messages[conversationId] = []
      } finally {
        this.messagesLoading = false
      }
    },

    async loadOlderMessages(conversationId) {
      if (!conversationId || this.loadingOlderMessages) return
      if (!this.hasMoreMessages[conversationId]) return

      this.loadingOlderMessages = true
      try {
        const existing = this.messages[conversationId] || []
        const oldest = existing.length > 0 ? existing[0].created_at : null
        if (!oldest) return

        const older = await chatHelpers.getMessages(conversationId, { limit: 20, before: oldest })
        if (older.length === 0) {
          this.hasMoreMessages[conversationId] = false
          return
        }

        const mapped = older.map(this._mapMessage)
        this.messages[conversationId] = [...mapped, ...existing]
        this.hasMoreMessages[conversationId] = older.length >= 20
      } catch (error) {
        console.error('Error loading older messages:', error)
      } finally {
        this.loadingOlderMessages = false
      }
    },

    async sendMessage(content) {
      if (!this.currentConversation || !content.trim()) return

      this.sendingMessage = true

      try {
        const adminMessage = '* ' + content.trim()
        const api = (await import('@/services/api')).default

        const twilioResponse = await api.post('/api/messages/test', {
          phone: this.currentConversation.patient_phone,
          message: adminMessage,
          clinic_id: this.currentConversation.clinic_id
        })

        if (!twilioResponse.data?.success) {
          throw new Error(twilioResponse.data?.error || 'Error enviando por Twilio')
        }

        if (this.currentConversation.needs_attention) {
          try {
            await chatHelpers.updateConversationAttention(
              this.currentConversation.clinic_id,
              this.currentConversation.patient_phone,
              false
            )
          } catch (attentionError) {
            // Ignore
          }
        }

        const conversation = this.conversations.find(c => c.id === this.currentConversation.id)
        if (conversation) {
          conversation.last_message = content.trim()
          conversation.last_message_time = new Date().toISOString()
          conversation.last_message_direction = 'outgoing'

          if (conversation.needs_attention) {
            conversation.needs_attention = false
            this.currentConversation.needs_attention = false
          }
        }

      } catch (error) {
        console.error('Error sending message:', error)
        this.error = error.message || 'Error al enviar mensaje'
        if (error.response?.data?.error) {
          this.error = `Error de WhatsApp: ${error.response.data.error}`
        }
        throw error
      } finally {
        this.sendingMessage = false
      }
    },

    selectConversation(conversation) {
      this.currentConversation = conversation
      if (conversation) {
        this.loadMessages(conversation.id)
        this.markAsRead(conversation.id)
        this.subscribeToConversationMessages(conversation.id)
      }
    },

    async markAsRead(conversationId) {
      try {
        await chatHelpers.markAsRead(conversationId)
        const conversation = this.conversations.find(c => c.id === conversationId)
        if (conversation) {
          conversation.unread_count = 0
        }
        const messages = this.messages[conversationId] || []
        messages.forEach(message => {
          if (message.direction === 'incoming') {
            message.read = true
          }
        })
      } catch (error) {
        console.error('Error marking as read:', error)
      }
    },

    setCurrentClinic(clinicId) {
      this.currentClinicId = clinicId
      this.currentConversation = null
      this.loadConversations()
      this.subscribeToMessages()
    },

    subscribeToMessages() {
      if (this.subscription) {
        this.unsubscribeFromMessages()
      }

      if (!this.currentClinicId) return

      this.subscription = realtimeSubscriptions.subscribeToConversations(
        this.currentClinicId,
        (payload) => {
          const conv = payload.new
          if (!conv || String(conv.clinic_id) !== String(this.currentClinicId)) return

          if (payload.eventType === 'INSERT') {
            if (!this.conversations.find(c => c.id === conv.id)) {
              this.conversations.unshift({
                ...conv,
                avatar: null,
                is_pinned: false,
                is_muted: false
              })
            }
          } else if (payload.eventType === 'UPDATE') {
            const oldConv = payload.old
            const index = this.conversations.findIndex(c => c.id === conv.id)

            if (index !== -1) {
              if (!oldConv?.needs_attention && conv.needs_attention) {
                this.showAttentionNotification(conv)
              }
              this.conversations[index] = {
                ...this.conversations[index],
                ...conv
              }
            } else {
              this.conversations.unshift({
                ...conv,
                avatar: null,
                is_pinned: false,
                is_muted: false
              })
            }
          }
        }
      )

      if (this.currentConversation) {
        this.subscribeToConversationMessages(this.currentConversation.id)
      }
    },

    subscribeToConversationMessages(conversationId) {
      if (!conversationId) return

      if (this.messageSubscription) {
        realtimeSubscriptions.unsubscribe(this.messageSubscription)
        this.messageSubscription = null
      }

      this.messageSubscription = realtimeSubscriptions.subscribeToMessages(
        conversationId,
        (payload) => {
          if (payload.eventType === 'INSERT') {
            const newMessage = payload.new

            if (!this.messages[conversationId]) {
              this.messages[conversationId] = []
            }

            if (!this.messages[conversationId].find(m => m.id === newMessage.id)) {
              this.messages[conversationId].push({
                ...newMessage,
                read: newMessage.direction === 'outgoing',
                status: newMessage.twilio_status || (newMessage.direction === 'outgoing' ? 'sent' : undefined)
              })

              this.messages[conversationId].sort((a, b) =>
                new Date(a.created_at) - new Date(b.created_at)
              )

              if (newMessage.direction === 'incoming' && this.currentConversation?.id !== conversationId) {
                const conversation = this.conversations.find(c => c.id === conversationId)
                if (conversation) {
                  conversation.unread_count = (conversation.unread_count || 0) + 1
                }
              }
            }
          } else if (payload.eventType === 'UPDATE') {
            const updatedMessage = payload.new

            if (this.messages[conversationId]) {
              const index = this.messages[conversationId].findIndex(m => m.id === updatedMessage.id)
              if (index !== -1) {
                this.messages[conversationId][index] = {
                  ...this.messages[conversationId][index],
                  ...updatedMessage,
                  status: updatedMessage.twilio_status || this.messages[conversationId][index].status
                }
              }
            }
          }
        }
      )
    },

    unsubscribeFromMessages() {
      if (this.messageSubscription) {
        realtimeSubscriptions.unsubscribe(this.messageSubscription)
        this.messageSubscription = null
      }
      if (this.subscription) {
        realtimeSubscriptions.unsubscribe(this.subscription)
        this.subscription = null
      }
    },

    async showAttentionNotification(conversation) {
      if (this.soundsEnabled) {
        try {
          await audioService.playAttentionAlert()
        } catch (error) {
          // Ignore
        }
      }

      if (window.Notification && Notification.permission === 'granted') {
        const notification = new Notification('Conversación requiere atención', {
          body: `${conversation.patient_name}: "${conversation.last_message?.substring(0, 100)}..."`,
          icon: '/icon-192.png',
          tag: `attention-${conversation.id}`,
          requireInteraction: true,
          silent: true
        })

        notification.onclick = () => {
          if (window.focus) window.focus()
          this.selectConversation(conversation)
          notification.close()
        }

        setTimeout(() => notification.close(), 10000)
      }
    },

    async requestNotificationPermission() {
      if (window.Notification && Notification.permission === 'default') {
        const permission = await Notification.requestPermission()
        return permission === 'granted'
      }
      return Notification.permission === 'granted'
    },

    async toggleConversationAttention(conversationId, needsAttention = null) {
      const conversation = this.conversations.find(c => c.id === conversationId)
      if (!conversation) return false

      const newAttentionState = needsAttention !== null ? needsAttention : !conversation.needs_attention

      try {
        await chatHelpers.updateConversationAttention(
          conversation.clinic_id,
          conversation.patient_phone,
          newAttentionState
        )

        conversation.needs_attention = newAttentionState
        if (this.currentConversation?.id === conversationId) {
          this.currentConversation.needs_attention = newAttentionState
        }

        if (!newAttentionState && this.soundsEnabled) {
          try {
            await audioService.playSuccessSound()
          } catch (error) {
            // Ignore
          }
        }

        return true
      } catch (error) {
        console.error('Error cambiando estado de atención:', error)
        return false
      }
    },

    setSoundsEnabled(enabled) {
      this.soundsEnabled = enabled
      audioService.setSoundsEnabled(enabled)
    },

    setSoundVolume(volume) {
      this.soundVolume = Math.max(0, Math.min(1, volume))
      audioService.setVolume(this.soundVolume)
    },

    async playTestSound() {
      if (this.soundsEnabled) {
        try {
          await audioService.playAttentionAlert()
        } catch (error) {
          // Ignore
        }
      }
    },

    initializeAudio() {
      audioService.setSoundsEnabled(this.soundsEnabled)
      audioService.setVolume(this.soundVolume)
    }
  }
})
